import React, { useEffect, useRef, useState } from 'react';
import { VENDEDORES } from './regras';

// Janela simples: fundo escuro, Esc fecha, foco no primeiro campo
export function Janela({ titulo, onFechar, children }) {
  const caixa = useRef(null);
  // A tela recarrega solicitações a cada 30 s: guardar o onFechar mais recente sem refazer o foco
  const fechar = useRef(onFechar);
  fechar.current = onFechar;

  useEffect(() => {
    const aoTeclar = (e) => { if (e.key === 'Escape') fechar.current(); };
    document.addEventListener('keydown', aoTeclar);
    const primeiro = caixa.current && caixa.current.querySelector('select:not([disabled]), input, textarea, button.cx-primario');
    if (primeiro) primeiro.focus();
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  return (
    <React.Fragment>
      <div className="cx-fundo cx-janela-fundo" onClick={onFechar} />
      <div className="cx-janela" role="dialog" aria-modal="true" aria-label={titulo} ref={caixa}>
        <h3>{titulo}</h3>
        {children}
      </div>
    </React.Fragment>
  );
}

// Pedir liberação de uma turma. Aberto da turma: a turma vem travada (como na tela antiga).
export function DialogoSolicitar({ curso, turmaFixa, turmasPossiveis, enviando, onEnviar, onFechar }) {
  const [turma, setTurma] = useState(turmaFixa || (turmasPossiveis[0] ? turmasPossiveis[0].Ano_Form : ''));
  const [contato, setContato] = useState('');
  const [motivo, setMotivo] = useState('');

  const enviar = (e) => {
    e.preventDefault();
    onEnviar({ turma, contato, motivo });
  };

  return (
    <Janela titulo="Solicitar liberação" onFechar={onFechar}>
      <dl className="cx-dados">
        <dt>Instituição</dt><dd>{curso.nomeintituicao}</dd>
        <dt>Curso</dt><dd>{curso.nomecurso}</dd>
        <dt>Cidade</dt><dd>{curso.nomecidade}</dd>
      </dl>
      <form onSubmit={enviar}>
        <label htmlFor="cx-sol-turma">Turma (ano/semestre)
          <select id="cx-sol-turma" value={turma} onChange={(e) => setTurma(e.target.value)} disabled={!!turmaFixa}>
            {turmaFixa && <option value={turmaFixa}>{turmaFixa}</option>}
            {!turmaFixa && turmasPossiveis.map(t => <option key={t.Ano_Form} value={t.Ano_Form}>{t.Ano_Form}</option>)}
          </select>
        </label>
        <label htmlFor="cx-sol-contato">Contato *
          <input id="cx-sol-contato" type="text" value={contato} onChange={(e) => setContato(e.target.value)} placeholder="Ex: João Silva - (11) 98765-4321" />
        </label>
        <label htmlFor="cx-sol-motivo">Motivo *
          <textarea id="cx-sol-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: Cliente demonstrou interesse em fechar o contrato." />
        </label>
        <div className="cx-rodape">
          <button type="button" className="cx-btn" onClick={onFechar}>Cancelar</button>
          <button type="submit" className="cx-btn cx-primario" disabled={enviando}>{enviando ? 'Enviando...' : 'Enviar solicitação'}</button>
        </div>
      </form>
    </Janela>
  );
}

// Avaliar solicitação pendente. Mostra também turma, contato e motivo que o vendedor escreveu.
export function DialogoAvaliar({ solicitacao, salvando, onResponder, onFechar }) {
  const [justificativa, setJustificativa] = useState('');
  const turma = solicitacao.ano_atendimento + (solicitacao.semestre_atendimento ? `/${solicitacao.semestre_atendimento}` : '');

  return (
    <Janela titulo="Avaliar solicitação" onFechar={onFechar}>
      <dl className="cx-dados">
        <dt>Instituição</dt><dd>{solicitacao.nome_instituicao}</dd>
        <dt>Curso</dt><dd>{solicitacao.nome_curso}</dd>
        <dt>Turma</dt><dd className="cx-num">{turma}</dd>
        <dt>Pedido por</dt><dd>{solicitacao.nome_vendedor_solicitante}</dd>
        <dt>Data</dt><dd>{new Date(solicitacao.data_solicitacao).toLocaleString()}</dd>
        <dt>Contato</dt><dd>{solicitacao.contato || '—'}</dd>
        <dt>Motivo</dt><dd>{solicitacao.motivo || '—'}</dd>
      </dl>
      <label htmlFor="cx-justificativa">Justificativa (opcional)
        <textarea id="cx-justificativa" value={justificativa} onChange={(e) => setJustificativa(e.target.value)} placeholder="Informe o motivo da decisão" />
      </label>
      <div className="cx-rodape">
        <button type="button" className="cx-btn" onClick={onFechar}>Cancelar</button>
        <button type="button" className="cx-btn cx-perigo" disabled={salvando} onClick={() => onResponder(false, justificativa)}>Rejeitar</button>
        <button type="button" className="cx-btn cx-primario" disabled={salvando} onClick={() => onResponder(true, justificativa)}>Aprovar</button>
      </div>
    </Janela>
  );
}

// Confirmação do +5d (a tela antiga usava a caixa de confirmação do navegador)
export function DialogoProrrogar({ salvando, onConfirmar, onFechar }) {
  return (
    <Janela titulo="Prorrogar prioridade?" onFechar={onFechar}>
      <p className="cx-dica">O prazo aumenta 5 dias. Só dá para prorrogar uma vez.</p>
      <div className="cx-rodape">
        <button type="button" className="cx-btn" onClick={onFechar}>Cancelar</button>
        <button type="button" className="cx-btn cx-primario" disabled={salvando} onClick={onConfirmar}>{salvando ? 'Prorrogando...' : 'Prorrogar +5d'}</button>
      </div>
    </Janela>
  );
}

export function DialogoRelacionar({ curso, turmasLivres, salvando, onEscolher, onFechar }) {
  const anoAtual = new Date().getFullYear();
  return (
    <Janela titulo="Relacionar vendedor" onFechar={onFechar}>
      <p className="cx-dica">
        {curso.nomecurso} · {curso.sigla_instituicao}.{' '}
        {turmasLivres.length === 0
          ? `Este curso não tem turma livre de ${anoAtual} em diante registrada no banco: não há onde gravar o vendedor relacionado.`
          : `Grava nas ${turmasLivres.length} turma(s) livre(s) de ${anoAtual} em diante (${turmasLivres.map(t => t.Ano_Form).join(', ')}). Turmas com negócio e o histórico não mudam.`}
      </p>
      <div className="cx-lista-vend">
        {VENDEDORES.map(v => (
          <button type="button" key={v.nome} disabled={salvando || turmasLivres.length === 0} onClick={() => onEscolher(v)}>{v.nome}</button>
        ))}
      </div>
      <div className="cx-rodape">
        {salvando && <span className="cx-dica">Atualizando vendedor...</span>}
        <button type="button" className="cx-btn" onClick={onFechar} disabled={salvando}>Cancelar</button>
      </div>
    </Janela>
  );
}
